import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Music as MusicIcon, Play, TrendingUp, Clock, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { playTrack, useGlobalPlayer, togglePlay } from '../components/GlobalMusicPlayer';
import SongContextMenu from '../components/SongContextMenu';
import AnimatedEqualizer from '../components/AnimatedEqualizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Music = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [newMusic, setNewMusic] = useState([]);
  const [trendingMusic, setTrendingMusic] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use global player state
  const { currentTrack, isPlaying } = useGlobalPlayer();

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-play track if passed in URL
  useEffect(() => {
    const trackId = searchParams.get('track');
    if (trackId && newMusic.length > 0) {
      const track = newMusic.find(t => t.id === trackId);
      if (track) {
        handlePlayTrack(track);
      }
    }
  }, [searchParams, newMusic]);

  const fetchData = async () => {
    try {
      const [newRes, trendingRes, playlistsRes] = await Promise.all([
        axios.get(`${API}/music/new?limit=20`),
        axios.get(`${API}/music?trending=true&limit=20`),
        axios.get(`${API}/playlists`),
      ]);
      setNewMusic(newRes.data);
      setTrendingMusic(trendingRes.data);
      setPlaylists(playlistsRes.data);
    } catch (error) {
      console.error('Error fetching music:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylistTracks = async (playlistId) => {
    try {
      const res = await axios.get(`${API}/music?playlist_id=${playlistId}`);
      setPlaylistTracks(res.data);
    } catch (error) {
      console.error('Error fetching playlist tracks:', error);
    }
  };

  const handlePlaylistClick = (playlist) => {
    setSelectedPlaylist(playlist);
    fetchPlaylistTracks(playlist.id);
  };

  const handlePlayTrack = async (track) => {
    // Check if it's the same track
    if (currentTrack?.id === track.id) {
      togglePlay();
      return;
    }

    // Play the track using global player
    playTrack({
      id: track.id,
      title: track.title,
      artist: track.artist,
      audio_url: track.file_url || track.embed_url,
      album_cover: track.cover_image
    });
    
    // Increment play count
    try {
      await axios.post(`${API}/music/${track.id}/play`);
    } catch (error) {
      console.error('Error incrementing play:', error);
    }
  };

  const handleDeleteTrack = (trackId) => {
    setNewMusic(prev => prev.filter(t => t.id !== trackId));
    setTrendingMusic(prev => prev.filter(t => t.id !== trackId));
    setPlaylistTracks(prev => prev.filter(t => t.id !== trackId));
  };

  const loadMusic = () => {
    fetchData();
  };

  const GlassCard = ({ children, className = '' }) => (
    <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl transition-all duration-300 ${className}`}>
      {children}
    </div>
  );

  const TrackCard = ({ track, index }) => {
    const isCurrentTrack = currentTrack?.id === track.id;
    
    return (
      <SongContextMenu song={track} onDelete={handleDeleteTrack} onRefresh={loadMusic}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all duration-300 group ${
            isCurrentTrack ? 'bg-white/10' : 'hover:bg-white/5'
          }`}
          onClick={() => handlePlayTrack(track)}
          data-testid={`track-${track.id}`}
        >
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/50 flex-shrink-0 relative">
            {track.cover_image ? (
              <img src={track.cover_image} alt={track.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <MusicIcon className="w-6 h-6 text-gray-600" />
              </div>
            )}
            <div className={`absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity ${
              isCurrentTrack && isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}>
              {isCurrentTrack && isPlaying ? (
                <AnimatedEqualizer color={theme.primary} />
              ) : (
                <Play className="w-5 h-5" style={{ color: theme.primary }} />
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`font-bold text-sm truncate ${isCurrentTrack ? '' : ''}`} style={isCurrentTrack ? { color: theme.primary } : {}}>
              {track.title}
            </h4>
            <p className="text-xs text-gray-500 truncate">{track.artist || 'Unknown Artist'}</p>
          </div>
          {isCurrentTrack && isPlaying ? (
            <AnimatedEqualizer color={theme.primary} />
          ) : (
            <div className="text-xs text-gray-600">
              {track.plays || 0} plays
            </div>
          )}
        </motion.div>
      </SongContextMenu>
    );
  };

  const EmbedPlayer = ({ track }) => {
    if (!track.embed_url) return null;

    const getEmbedHtml = () => {
      const url = track.embed_url;
      
      if (track.embed_type === 'spotify' || url.includes('spotify.com')) {
        const match = url.match(/track\/([a-zA-Z0-9]+)/);
        if (match) {
          return `<iframe style="border-radius:12px" src="https://open.spotify.com/embed/track/${match[1]}" width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>`;
        }
      }
      
      if (track.embed_type === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
        if (match) {
          return `<iframe width="100%" height="152" src="https://www.youtube.com/embed/${match[1]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
        }
      }

      if (track.embed_type === 'soundcloud' || url.includes('soundcloud.com')) {
        return `<iframe width="100%" height="166" scrolling="no" frameborder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=${theme.primary.replace('#', '')}&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"></iframe>`;
      }

      return null;
    };

    const embedHtml = getEmbedHtml();
    if (!embedHtml) return null;

    return (
      <div 
        className="rounded-lg overflow-hidden"
        dangerouslySetInnerHTML={{ __html: embedHtml }}
      />
    );
  };

  return (
    <div className="py-4">
      <div className="max-w-6xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 
            className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1"
            style={{ color: theme.primary, textShadow: theme.glow }}
            data-testid="music-title"
          >
            Music Library
          </h1>
          <p className="text-sm text-gray-400">Discover tracks from DJ808 & CRG Productions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Playlists Sidebar */}
          <div className="lg:col-span-1">
            <GlassCard className="p-4">
              <h2 className="font-bold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <List className="w-5 h-5" />
                Playlists
              </h2>
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {playlists.map((playlist) => (
                    <div
                      key={playlist.id}
                      onClick={() => handlePlaylistClick(playlist)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedPlaylist?.id === playlist.id ? 'bg-white/10' : 'hover:bg-white/5'
                      }`}
                      data-testid={`playlist-${playlist.id}`}
                    >
                      <h4 className="font-bold text-sm">{playlist.name}</h4>
                      <p className="text-xs text-gray-500">{playlist.description}</p>
                    </div>
                  ))}
                  {playlists.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No playlists yet</p>
                  )}
                </div>
              </ScrollArea>
            </GlassCard>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {selectedPlaylist ? (
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-bold text-xl" style={{ color: theme.primary }}>{selectedPlaylist.name}</h2>
                    <p className="text-sm text-gray-400">{selectedPlaylist.description}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    onClick={() => setSelectedPlaylist(null)}
                    className="text-gray-400"
                  >
                    Back
                  </Button>
                </div>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-1">
                    {playlistTracks.map((track, index) => (
                      <TrackCard key={track.id} track={track} index={index} />
                    ))}
                    {playlistTracks.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No tracks in this playlist</p>
                    )}
                  </div>
                </ScrollArea>
              </GlassCard>
            ) : (
              <Tabs defaultValue="new" className="w-full">
                <TabsList className="bg-black/40 border border-white/10 mb-4">
                  <TabsTrigger 
                    value="new" 
                    className="data-[state=active]:bg-white/10"
                    data-testid="tab-new-music"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    New Music
                  </TabsTrigger>
                  <TabsTrigger 
                    value="trending"
                    className="data-[state=active]:bg-white/10"
                    data-testid="tab-trending"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Trending
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="new">
                  <GlassCard className="p-4">
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-1">
                        {newMusic.map((track, index) => (
                          <TrackCard key={track.id} track={track} index={index} />
                        ))}
                        {newMusic.length === 0 && (
                          <p className="text-gray-500 text-center py-8">No music yet</p>
                        )}
                      </div>
                    </ScrollArea>
                  </GlassCard>
                </TabsContent>

                <TabsContent value="trending">
                  <GlassCard className="p-4">
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-1">
                        {trendingMusic.map((track, index) => (
                          <TrackCard key={track.id} track={track} index={index} />
                        ))}
                        {trendingMusic.length === 0 && (
                          <p className="text-gray-500 text-center py-8">No trending tracks yet</p>
                        )}
                      </div>
                    </ScrollArea>
                  </GlassCard>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Music;
